import { DOCUMENT, effect, inject, Injectable, Renderer2, Signal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DeviceSizeService, RmaBreakpoint } from '@rma/dashboard/generic/util/device-size';
import { RouteUtilityService } from '@rma/dashboard/generic/util/router-utils';
import { SessionStorageService } from '@rma/dashboard/generic/util/storage';
import { rmaShareReplay } from '@rma/generic/rxjs/operators/rma-share-replay';
import { combineLatest, map, startWith } from 'rxjs';

@Injectable()
export class LayoutService {
  private readonly document = inject(DOCUMENT);
  private readonly routeUtils = inject(RouteUtilityService);
  private readonly deviceSizeService = inject(DeviceSizeService);
  private readonly renderer = inject(Renderer2);
  private readonly sessionStorage = inject(SessionStorageService);

  private readonly mobileSideNavVisibleSignal = signal(false);
  private readonly desktopSideNavCollapsedSignal = signal(false);

  public readonly showHeader: Signal<boolean>;
  public readonly showSideNav: Signal<boolean>;

  public get mobileSideNavVisible(): Signal<boolean> {
    return this.mobileSideNavVisibleSignal.asReadonly();
  }

  public get desktopSideNavCollapsed(): Signal<boolean> {
    return this.desktopSideNavCollapsedSignal.asReadonly();
  }

  constructor() {
    const showHeader$ = this.routeUtils.routeData$.pipe(
      startWith({ hideRmaHeader: false }),
      map(({ hideRmaHeader }) => !hideRmaHeader),
      rmaShareReplay(),
    );

    this.showHeader = toSignal(showHeader$, { initialValue: true });

    const showSideNav$ = combineLatest({
      showRmaHeader: showHeader$,
      showRmaSideNav: this.routeUtils.routeData$.pipe(
        startWith({ hideRmaSideNav: false }),
        map(({ hideRmaSideNav }) => !hideRmaSideNav),
      ),
    }).pipe(
      map(({ showRmaHeader, showRmaSideNav }) => showRmaHeader && showRmaSideNav),
      rmaShareReplay(),
    );

    this.showSideNav = toSignal(showSideNav$, { initialValue: true });

    const sideNavCollapsed = this.sessionStorage.getBoolean('side-nav-collapsed');
    this.desktopSideNavCollapsedSignal.set(sideNavCollapsed);

    effect(() => {
      const mobileSideNavVisible = this.mobileSideNavVisible();

      // prevent scrolling on the background
      if (mobileSideNavVisible) {
        this.renderer.setStyle(this.document.documentElement, 'overflow', 'hidden');
      } else {
        this.renderer.removeStyle(this.document.documentElement, 'overflow');
      }
    });

    // Honour preference on page refresh
    effect(() => {
      const desktopSideNavCollapsed = this.desktopSideNavCollapsedSignal();
      this.sessionStorage.setBoolean('side-nav-collapsed', desktopSideNavCollapsed);
    });
  }

  public mobileToggleSideNav() {
    this.mobileSideNavVisibleSignal.update((visible) => !visible);
    this.desktopSideNavCollapsedSignal.set(false);
  }

  public desktopToggleSideNav() {
    this.desktopSideNavCollapsedSignal.update((collapsed) => !collapsed);
    this.mobileSideNavVisibleSignal.set(false);
  }

  public onNavigation() {
    if (this.deviceSizeService.isMatched(RmaBreakpoint['<LG'])) {
      this.mobileToggleSideNav();
    }
  }
}
